/**
 * Analytics filter state — local to the Analytics page.
 *
 * Uses a Context because 15+ chart components subscribe to the same filter
 * snapshot. Prop-drilling would mean touching every chart when a filter is
 * added or removed.
 *
 * Charts consume via `useAnalyticsFilters()`. Filter bar updates via
 * `useAnalyticsFilterActions()`.
 */

import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import type { AnalyticsFilterState, DqStatusFilter, VesselTypeKey } from "@/types/analytics";

const DEFAULT_FILTERS: AnalyticsFilterState = {
  coverholder: "all",
  dateFrom: "2025-03-14",
  dateTo: "2026-03-13",
  vesselType: "all",
  dqStatus: "all",
};

interface FilterActions {
  setCoverholder: (id: string) => void;
  setDateFrom: (iso: string) => void;
  setDateTo: (iso: string) => void;
  setVesselType: (key: VesselTypeKey) => void;
  setDqStatus: (status: DqStatusFilter) => void;
  clear: (key: keyof AnalyticsFilterState) => void;
  reset: () => void;
}

const FilterStateContext = createContext<AnalyticsFilterState | null>(null);
const FilterActionsContext = createContext<FilterActions | null>(null);

export function AnalyticsFilterProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AnalyticsFilterState>(DEFAULT_FILTERS);

  const actions = useMemo<FilterActions>(
    () => ({
      setCoverholder: (id) => setState((s) => ({ ...s, coverholder: id })),
      setDateFrom: (iso) => setState((s) => ({ ...s, dateFrom: iso })),
      setDateTo: (iso) => setState((s) => ({ ...s, dateTo: iso })),
      setVesselType: (key) => setState((s) => ({ ...s, vesselType: key })),
      setDqStatus: (status) => setState((s) => ({ ...s, dqStatus: status })),
      clear: (key) => setState((s) => ({ ...s, [key]: DEFAULT_FILTERS[key] })),
      reset: () => setState(DEFAULT_FILTERS),
    }),
    []
  );

  return (
    <FilterStateContext.Provider value={state}>
      <FilterActionsContext.Provider value={actions}>{children}</FilterActionsContext.Provider>
    </FilterStateContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAnalyticsFilters(): AnalyticsFilterState {
  const ctx = useContext(FilterStateContext);
  if (!ctx) throw new Error("useAnalyticsFilters must be used inside AnalyticsFilterProvider");
  return ctx;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAnalyticsFilterActions(): FilterActions {
  const ctx = useContext(FilterActionsContext);
  if (!ctx)
    throw new Error("useAnalyticsFilterActions must be used inside AnalyticsFilterProvider");
  return ctx;
}

/** Convenience hook: call when you only need the "is a filter active" set for active-tag display. */
// eslint-disable-next-line react-refresh/only-export-components
export function useActiveFilterTags(): Array<{ key: keyof AnalyticsFilterState; label: string }> {
  const f = useAnalyticsFilters();
  return useMemo(() => {
    const tags: Array<{ key: keyof AnalyticsFilterState; label: string }> = [];
    if (f.coverholder !== DEFAULT_FILTERS.coverholder)
      tags.push({ key: "coverholder", label: `Coverholder: ${f.coverholder}` });
    if (f.vesselType !== DEFAULT_FILTERS.vesselType)
      tags.push({ key: "vesselType", label: `Type: ${f.vesselType}` });
    if (f.dqStatus !== DEFAULT_FILTERS.dqStatus)
      tags.push({ key: "dqStatus", label: `DQ: ${f.dqStatus}` });
    return tags;
  }, [f]);
}

export { DEFAULT_FILTERS };
